/* EditOrgComponent.js */

"use strict";
const _ = require("lodash");
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./EditOrgRenderer");

class EditOrgComponent extends EventEmitter {
    constructor(
        selectedOrgInfo,
        retentionPolicyEntity,
        userInfo,
        uiUtils,
        logger,
        modalUserFeedback,
        samloAuthExists,
        isDCEnabled,
        credentialsList,
        passwordPolicy
    ) {
        super();
        this._selectedOrgInfo = selectedOrgInfo;
        this._userInfo = userInfo;
        this._saveModel = _.pick(this._selectedOrgInfo, ["ldap", "radius", "id"]);
        this._logger = logger;
        this._modalUserFeedback = modalUserFeedback;
        this._samloAuthExists = samloAuthExists || false;
        this._isDCEnabled = isDCEnabled;
        this._credentialsList = credentialsList;
        this._passwordPolicy = passwordPolicy;
        this._oldPWPolicy = passwordPolicy;
        this._renderer = new Renderer(this, retentionPolicyEntity, uiUtils);

        this._registerListeners();
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {}
    _unregisterListeners() {
        this.removeAllListeners();
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    get modalUserFeedback() {
        return this._modalUserFeedback;
    }

    get logger() {
        return this._logger;
    }

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    shutdown() {
        this._unregisterListeners();
        let prom = this._renderer._shutdown().then(
            () => {
                this._selectedOrgInfo = undefined;
                this._modalUserFeedback = undefined;
                this._logger = undefined;
                this._saveModel = undefined;
                this._renderer = undefined;
            },
            (err) => {
                this._logger.warn(
                    {error: err},
                    "EditOrgComponent.shutdown() Error shutting down EditOrgComponent."
                );
            }
        );
        return prom;
    }

    changeScreen(displayType) {
        this.emit("SwitchContent", displayType);
    }

    saveSelectedOrg() {
        this.emit("SaveSelectedOrg", this._saveModel, this._passwordPolicy);
    }

    deleteSelectedOrg() {
        this.emit("DeleteSelectedOrg");
    }

    get selectedOrgInfo() {
        return this.getSelectedOrgInfo();
    }

    set selectedOrgInfo(newInfo) {
        this.setSelectedOrgInfo(newInfo);
    }

    get passwordPolicy() {
        return this.getPasswordPolicy();
    }

    set passwordPolicy(newInfo) {
        this.setPasswordPolicy(newInfo);
    }

    getSelectedOrgInfo() {
        return this._selectedOrgInfo;
    }

    setSelectedOrgInfo(newInfo) {
        this._selectedOrgInfo = newInfo;
        const authMethod = _.get(this.selectedOrgInfo, "authMethod");
        if (["gluware", "ldap", "radius"].indexOf(authMethod) < 0) {
            // Managing legacy data without authMethod
            const hasLdapConfig = !_.isEmpty(_.get(this.selectedOrgInfo, "ldap"));
            const hasRadiusConfig = !_.isEmpty(_.get(this.selectedOrgInfo, "radius"));
            if (hasLdapConfig !== hasRadiusConfig) {
                if (hasLdapConfig) {
                    this.selectedOrgInfo.authMethod = "ldap";
                } else {
                    this.selectedOrgInfo.authMethod = "radius";
                }
            } else {
                this.selectedOrgInfo.authMethod = "gluware";
            }
        }
        this._saveModel = _.pick(this._selectedOrgInfo, ["ldap", "radius", "id", "authMethod"]);
        this.renderer.render();
    }

    setCredentialsList(credentialsList) {
        this._credentialsList = credentialsList || [];
    }

    getPasswordPolicy() {
        return this._passwordPolicy;
    }

    setPasswordPolicy(newInfo) {
        this._oldPWPolicy = newInfo;
        this._passwordPolicy = newInfo;
        this.emit("PasswordPolicyChanged");
    }

    updateOrgSaveModelField(property, value, keepMessage) {
        const isEmpty = (val) => val === null || val === undefined || val === "";
        const pristineValue = _.get(this._selectedOrgInfo, property);
        if (_.isEqual(value, pristineValue) || (isEmpty(value) && isEmpty(pristineValue))) {
            this.removeOrgSaveModelField(property, keepMessage);
        } else {
            _.set(this._saveModel, property, value);
            this.emit("SaveModelUpdated", keepMessage);
            this.checkNotDirty();
        }
    }

    removeOrgSaveModelField(property, keepMessage) {
        if (_.has(this._saveModel, property)) {
            _.unset(this._saveModel, property);
            this.emit("SaveModelUpdated", keepMessage);
            this.checkNotDirty();
        }
    }

    checkNotDirty() {
        let saveModelClone = Object.assign({}, this._saveModel);
        delete saveModelClone.id;
        if (_.isEmpty(saveModelClone)) {
            this.emit("SaveModelPristine");
            return true;
        } else {
            this.emit("SaveModelUpdated", true);
        }
    }

    updateOrgPWSaveModelField(formValues, keepMessage) {
        this._passwordPolicy = formValues;
        if (this._isPWPolicyPristine()) {
            this.emit("SaveModelPristine");
            return true;
        } else {
            this.emit("SaveModelUpdated", keepMessage);
        }
    }

    clearOrgSaveModel() {
        this._saveModel = _.pick(this._selectedOrgInfo, ["ldap", "radius", "id", "authMethod"]);
        this.emit("SaveModelCleared");
    }

    setLDAPConfig(obj) {
        this._LDAPConfig = obj;
    }

    updateLdapProperty(prop, value) {
        this.updateOrgSaveModelField("ldap." + prop, value);
    }

    setRADIUSConfig(obj) {
        this._RADIUSConfig = obj;
    }

    updateRadiusProperty(prop, value) {
        this.updateOrgSaveModelField("radius." + prop, value);
    }

    get userInfo() {
        return this.getUserInfo();
    }

    getUserInfo() {
        return this._userInfo;
    }

    setGluAPIEnabled(value) {
        return this.updateOrgSaveModelField("enableApi", Boolean(value));
    }

    getGluAPIEnabled() {
        return this.selectedOrgInfo.enableApi;
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    emitTestLdap(data) {
        this.emit("testLDAP", data);
    }

    emitTestRadius(data) {
        this.emit("testRADIUS", data);
    }

    _isPWPolicyPristine() {
        return (
            this._passwordPolicy.minPasswordLength === this._oldPWPolicy.minPasswordLength &&
            this._passwordPolicy.minUpperCaseChar === this._oldPWPolicy.minUpperCaseChar &&
            this._passwordPolicy.minLowerCaseChar === this._oldPWPolicy.minLowerCaseChar &&
            this._passwordPolicy.minNumericChar === this._oldPWPolicy.minNumericChar &&
            this._passwordPolicy.minSpecialChar === this._oldPWPolicy.minSpecialChar
        );
    }

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    testLdapError(error) {
        this.emit("TestLdapError", error);
    }
    testRadiusError(error) {
        this.emit("TestRadiusError", error);
    }
    crudError(err) {
        switch (err.type) {
            case "delete":
                this.emit("DeleteOrgError", err.error);
                break;
            case "save":
                this.emit("SaveOrgError", err.error);
                break;
        }
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}
module.exports = EditOrgComponent;
